import { Inventory2Outlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import type {
  DeliveryItem,
  RecallCheckMatchRecall,
} from '../../../services/goods-receiving/goods-receiving.types';
import { AddItemButton } from '../components/add-item-button';
import { AddItemDialog } from '../components/add-item-dialog';
import { AddItemIntroCard } from '../components/add-item-dialog-steps/add-item-intro-card';
import { AiGapBanner } from '../components/add-item-dialog-steps/ai-gap-banner';
import type { AllergenEntry } from '../components/add-item-dialog-steps/steps/allergen-selection-step';
import { AllergenSelectionStep } from '../components/add-item-dialog-steps/steps/allergen-selection-step';
import { BasicDataStep } from '../components/add-item-dialog-steps/steps/ddt-document';
import { NonComplianceStep } from '../components/add-item-dialog-steps/steps/non-compliance-step';
import { PhotoStep } from '../components/add-item-dialog-steps/steps/product-photo-step';
import { TraceabilityStep } from '../components/add-item-dialog-steps/steps/product-traceability-step';
import { DdtPagesBanner } from '../components/ddt-pages-banner';
import { ItemsEmptyState } from '../components/items-empty-state';
import { DeliveryItemCard, getItemsGate, getMissingFields } from '../components/local-item-card';
import { WizardNavigation } from '../components/wizard-navigation';
import { WizardStepHeader } from '../components/wizard-step-header';
import { type AddItemFormData, getDefaultFormValues } from '../hooks/use-add-item-form';
import {
  useAddItem,
  useDeleteItem,
  useMarkItemsNoExpiry,
  useUpdateDeliveryItem,
} from '../hooks/use-delivery-note-mutations';
import { usePatchDraft } from '../hooks/use-draft-mutations';
import { useDraftRecallMatches } from '../hooks/use-draft-recall-matches';
import { getStepRoute, useWizardDraft } from '../hooks/use-wizard-draft';
import { buildAppendPagesQuery } from '../utils/ddt-pages-notice';
import { WizardLayout } from './wizard-layout';

type AllergenEntryInput = {
  allergenId?: string;
  allergenCode?: string;
  category: 'CONTAINS' | 'MAY_CONTAIN';
};

type ExtractedAllergenEntry = AllergenEntryInput;

function itemToFormData(item: DeliveryItem): AddItemFormData {
  return {
    productName: item.productName,
    quantity: String(item.quantity),
    unit: item.unit,
    batchNumber: item.batchNumber ?? '',
    expiryDate: item.expiryDate ?? '',
    hasNoExpiry: item.hasNoExpiry ?? false,
    isLooseProduce: false,
    isDamaged: false,
    isTemperatureNonCompliant: false,
    isHygieneNonCompliant: false,
    unitPrice: item.unitPrice != null ? String(item.unitPrice) : '',
    lineTotal: item.lineTotal != null ? String(item.lineTotal) : '',
    currency: 'EUR',
    labelPhotos: [],
  };
}

function buildItemData(data: AddItemFormData, allergens: AllergenEntry[] | null) {
  const quantity = data.quantity ? Number.parseFloat(data.quantity) || 1 : 1;
  const hasNoExpiry = data.hasNoExpiry === true;
  return {
    productName: data.productName.trim(),
    quantity,
    unit: data.unit?.trim() || 'PZ',
    batchNumber: data.batchNumber?.trim() ?? '',
    expiryDate: hasNoExpiry ? '' : data.expiryDate || '',
    hasNoExpiry,
    isDamaged: data.isDamaged ?? false,
    isTemperatureNonCompliant: data.isTemperatureNonCompliant ?? false,
    isHygieneNonCompliant: data.isHygieneNonCompliant ?? false,
    unitPrice: data.unitPrice ? Number.parseFloat(data.unitPrice) : undefined,
    lineTotal: data.lineTotal ? Number.parseFloat(data.lineTotal) : undefined,
    currency: data.currency || 'EUR',
    allergens: allergens
      ? allergens.map((a) => ({ allergenId: a.allergenId, category: a.category }))
      : undefined,
  };
}

function toUpdatePayload(
  itemId: string,
  deliveryNoteId: string,
  d: ReturnType<typeof buildItemData>,
) {
  return {
    itemId,
    deliveryNoteId,
    data: {
      productName: d.productName,
      quantity: d.quantity,
      unit: d.unit,
      batchNumber: d.batchNumber || null,
      expiryDate: d.hasNoExpiry ? null : d.expiryDate || null,
      hasNoExpiry: d.hasNoExpiry,
      unitPrice: d.unitPrice,
      lineTotal: d.lineTotal,
      currency: d.currency,
      allergens: d.allergens ?? [],
    },
  };
}

function useItemDialog(
  draftId: string | null,
  draft?: {
    extractionResult?: {
      inferredAllergens?: { productName: string; allergens?: ExtractedAllergenEntry[] }[];
    } | null;
  },
) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingAllergens, setEditingAllergens] = useState<ExtractedAllergenEntry[] | undefined>(
    undefined,
  );
  // AI Act art. 50: true when the shown allergens come from `inferredAllergens`
  // (LLM-generated) instead of values the operator saved on the item.
  const [allergensAreAiInferred, setAllergensAreAiInferred] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const currentAllergensRef = useRef<AllergenEntry[] | null>(null);

  const addItem = useAddItem();
  const updateItem = useUpdateDeliveryItem();

  const form = useForm<AddItemFormData>({
    mode: 'onChange',
    defaultValues: getDefaultFormValues(),
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    trigger,
    formState: { isValid, isSubmitting },
  } = form;

  const handleAllergensChange = (allergens: AllergenEntry[] | null) => {
    currentAllergensRef.current = allergens;
  };

  const findInferredAllergens = (productName: string): ExtractedAllergenEntry[] | undefined => {
    const match = draft?.extractionResult?.inferredAllergens?.find(
      (entry) => entry.productName === productName,
    );
    return match?.allergens;
  };

  const openDialogForCreate = () => {
    setEditingItemId(null);
    setEditingAllergens(undefined);
    setAllergensAreAiInferred(false);
    reset(getDefaultFormValues());
    setAccordionExpanded(false);
    setDialogOpen(true);
    setTimeout(() => trigger(), 0);
  };

  const openDialogForEdit = (item: DeliveryItem) => {
    setEditingItemId(item.id);
    const persistedAllergens =
      item.allergens && item.allergens.length > 0 ? item.allergens : undefined;
    const inferredAllergens = persistedAllergens
      ? undefined
      : findInferredAllergens(item.productName);
    setEditingAllergens(persistedAllergens ?? inferredAllergens);
    setAllergensAreAiInferred((inferredAllergens?.length ?? 0) > 0);
    reset(itemToFormData(item));
    setAccordionExpanded(false);
    setDialogOpen(true);
    setTimeout(() => trigger(), 0);
  };

  const handleOpenDialog = (itemToEdit?: DeliveryItem) => {
    currentAllergensRef.current = null;
    if (itemToEdit) {
      openDialogForEdit(itemToEdit);
    } else {
      openDialogForCreate();
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItemId(null);
    setEditingAllergens(undefined);
    setAllergensAreAiInferred(false);
    setAccordionExpanded(false);
    reset(getDefaultFormValues());
    currentAllergensRef.current = null;
  };

  const resetDialogState = () => {
    reset(getDefaultFormValues());
    setDialogOpen(false);
    setEditingItemId(null);
    currentAllergensRef.current = null;
  };

  const onSubmit = (data: AddItemFormData) => {
    if (!draftId) {
      return;
    }
    const itemData = buildItemData(data, currentAllergensRef.current);

    if (editingItemId) {
      updateItem.mutate(toUpdatePayload(editingItemId, draftId, itemData), {
        onSuccess: resetDialogState,
      });
      return;
    }

    addItem.mutate(
      {
        deliveryNoteId: draftId,
        item: itemData,
        files: data.labelPhotos.length > 0 ? data.labelPhotos : undefined,
      },
      { onSuccess: resetDialogState },
    );
  };

  const handleFormSubmit = () => {
    handleSubmit(onSubmit)();
  };

  return {
    dialogOpen,
    editingItemId,
    editingAllergens,
    allergensAreAiInferred,
    accordionExpanded,
    setAccordionExpanded,
    form,
    isValid,
    isSubmitting,
    handleOpenDialog,
    handleCloseDialog,
    handleFormSubmit,
    handleAllergensChange,
    addItem,
    updateItem,
  };
}

export function ItemsStepPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const { data: draft } = useWizardDraft(draftId);
  const patchDraft = usePatchDraft();
  const deleteItem = useDeleteItem();

  const isExtracting = draft?.extractionStatus === 'PENDING';
  const items: DeliveryItem[] = draft?.items ?? [];

  const recallMatchesQuery = useDraftRecallMatches(draftId, items.length);
  const recallByItemId = new Map<string, RecallCheckMatchRecall[]>();
  recallMatchesQuery.data?.matches.forEach((m) => {
    recallByItemId.set(m.itemId, m.recalls);
  });

  const dialog = useItemDialog(draftId, draft ?? undefined);
  const markItemsNoExpiry = useMarkItemsNoExpiry();
  const [expiryConfirmOpen, setExpiryConfirmOpen] = useState(false);

  const { hasBlockingMissing, hasMissingExpiry, canGoNext } = getItemsGate(items);

  const proceedToChecklist = () => {
    if (!draftId) {
      return;
    }
    patchDraft.mutate(
      {
        id: draftId,
        data: { currentStep: 'checklist', itemsCount: items.length, allItemsValidated: true },
      },
      {
        onSuccess: () =>
          navigate(`${getStepRoute('checklist')}?draftId=${draftId}`, { flushSync: true }),
      },
    );
  };

  const handleNext = () => {
    if (!draftId) {
      return;
    }
    if (hasMissingExpiry) {
      setExpiryConfirmOpen(true);
      return;
    }
    proceedToChecklist();
  };

  // Confirming "ignore expiry" records the consent as hasNoExpiry=true on the
  // affected items — otherwise the backend rejects the final receipt completion.
  const handleConfirmIgnoreExpiry = () => {
    if (!draftId) {
      return;
    }
    const itemIds = items
      .filter((item) => getMissingFields(item).includes('expiryDate'))
      .map((item) => item.id);
    markItemsNoExpiry.mutate(
      { itemIds, deliveryNoteId: draftId },
      {
        onSuccess: () => {
          setExpiryConfirmOpen(false);
          proceedToChecklist();
        },
      },
    );
  };

  const handleBack = () => {
    navigate(`${getStepRoute('document')}?draftId=${draftId}`, { flushSync: true });
  };

  return (
    <WizardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ItemsStepHeader />

        {isExtracting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('goodsReceiving.extractingItems')}
            </Typography>
          </Box>
        )}

        {!isExtracting && (
          <DdtPagesBanner
            pages={draft?.extractionResult?.document?.pages}
            onAddPages={() =>
              navigate(`${getStepRoute('document')}?${buildAppendPagesQuery(draftId ?? '')}`, {
                flushSync: true,
              })
            }
          />
        )}

        {items.length === 0 && !isExtracting ? (
          <ItemsEmptyState onAddClick={() => dialog.handleOpenDialog()} />
        ) : (
          <>
            <ItemsList
              items={items}
              isMobile={isMobile}
              recallByItemId={recallByItemId}
              onRemoveItem={(itemId) => {
                if (!draftId) {
                  return;
                }
                deleteItem.mutate({ itemId, draftId });
              }}
              onEditItem={dialog.handleOpenDialog}
            />
            <AddItemButton isMobile={isMobile} onClick={() => dialog.handleOpenDialog()} />
          </>
        )}
      </Box>
      <AddItemDialog
        open={dialog.dialogOpen}
        onClose={dialog.handleCloseDialog}
        isSubmitting={
          dialog.isSubmitting || dialog.addItem.isPending || dialog.updateItem.isPending
        }
        isValid={dialog.isValid}
        onSubmit={dialog.handleFormSubmit}
        isEditMode={!!dialog.editingItemId}
      >
        <FormProvider {...dialog.form}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <AddItemIntroCard />
            <PhotoStep isEditMode={!!dialog.editingItemId} />
            <AiGapBanner />
            <BasicDataStep />
            <TraceabilityStep
              isMobile={isMobile}
              wizardContext={{
                supplierName: draft?.supplier?.name,
                documentDate: draft?.date ?? undefined,
                documentNumber: draft?.documentNumber ?? undefined,
              }}
            />
            <AllergenSelectionStep
              initialAllergens={dialog.editingAllergens}
              onAllergensChange={dialog.handleAllergensChange}
              aiPrefilled={dialog.allergensAreAiInferred}
            />
            <Accordion
              expanded={dialog.accordionExpanded}
              onChange={(_e, expanded) => dialog.setAccordionExpanded(expanded)}
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                '&::before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 56 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'medium', color: 'text.secondary' }}
                  >
                    {t('goodsReceiving.optionalFields')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {t('goodsReceiving.optionalFieldsHelper')}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 2, pb: 2 }}
              >
                <NonComplianceStep />
              </AccordionDetails>
            </Accordion>
          </Box>
        </FormProvider>
      </AddItemDialog>
      {items.length > 0 && hasBlockingMissing && (
        <Typography
          variant="body2"
          sx={{ color: 'warning.main', textAlign: 'center', mt: 2, pb: 1 }}
        >
          {t('goodsReceiving.itemsIncompleteWarning')}
        </Typography>
      )}
      <WizardNavigation
        canGoBack={true}
        canGoNext={canGoNext}
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={t('goodsReceiving.nextStep')}
        isLoading={patchDraft.isPending}
      />
      <Dialog
        open={expiryConfirmOpen}
        onClose={() => setExpiryConfirmOpen(false)}
        aria-labelledby="expiry-confirm-title"
        aria-describedby="expiry-confirm-description"
      >
        <DialogTitle id="expiry-confirm-title">
          {t('goodsReceiving.expiryConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="expiry-confirm-description">
            {t('goodsReceiving.expiryConfirmBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button
            onClick={() => setExpiryConfirmOpen(false)}
            disabled={markItemsNoExpiry.isPending}
            fullWidth={isMobile}
            sx={{ minHeight: 48 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmIgnoreExpiry}
            disabled={markItemsNoExpiry.isPending}
            fullWidth={isMobile}
            sx={{ minHeight: 48 }}
          >
            {t('goodsReceiving.expiryConfirmConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </WizardLayout>
  );
}

function ItemsStepHeader() {
  const { t } = useTranslation('goodsReceiving');

  return (
    <WizardStepHeader
      step="items"
      icon={<Inventory2Outlined />}
      title={t('itemsStepTitle')}
      subtitle={t('itemsStepSubtitle')}
    />
  );
}

interface ItemsListProps {
  items: DeliveryItem[];
  isMobile: boolean;
  recallByItemId: Map<string, RecallCheckMatchRecall[]>;
  onRemoveItem: (itemId: string) => void;
  onEditItem: (item: DeliveryItem) => void;
}

function ItemsList({ items, isMobile, recallByItemId, onRemoveItem, onEditItem }: ItemsListProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item) => (
        <DeliveryItemCard
          key={item.id}
          item={item}
          isMobile={isMobile}
          onRemove={() => onRemoveItem(item.id)}
          onEdit={() => onEditItem(item)}
          recallMatches={recallByItemId.get(item.id)}
        />
      ))}
    </Box>
  );
}
